import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { commonErrorResponses, requiredPayloadEnvelope } from '../envelope.js';
const c = initContract();
const broadcastFilterSchema = z.enum(['all', 'adult', 'password']);
const ingestStreamTargetSchema = z.object({
    platform: z.string().min(1),
    nativeId: z.string().min(1),
});
const ingestFetchFailuresSchema = z.object({
    count: z.number().int().nonnegative(),
    lastAt: z.number().nullable(),
});
export const ingestBroadcastItemSchema = z.object({
    bjId: z.string(),
    broadNo: z.string(),
    userNick: z.string(),
    title: z.string(),
    thumbUrl: z.string().nullable(),
    viewerCount: z.number(),
    tags: z.array(z.string()),
    grade: z.string(),
    isPassword: z.boolean(),
    /** fleet 수집 후보(일반·비번없음) */
    collectable: z.boolean(),
    collecting: z.boolean(),
    throughputEventsPerSec: z.number().nullable(),
});
export const ingestBroadcastsMetaSchema = z.object({
    filter: broadcastFilterSchema,
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    total: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
    throughputTotal: z.number().nullable(),
    collectorOk: z.boolean(),
    listCachedAt: z.string().nullable(),
});
export const ingestDonationSchema = z.object({
    id: z.number(),
    ts: z.number(),
    userId: z.string().nullable(),
    nickname: z.string().nullable(),
    count: z.number().nullable(),
    cmd4: z.string().nullable(),
});
export const ingestSuspendedTargetSchema = ingestStreamTargetSchema.extend({
    reason: z.string().nullable(),
    updatedAt: z.number(),
});
export const ingestFleetWorkerSchema = z.object({
    workerId: z.string(),
    assignedCount: z.number(),
    activeCount: z.number(),
    maxCollectors: z.number().int().positive().nullable(),
    loadPerSec: z.number(),
    meaningfulPerSec: z.number(),
    recentFetchFailures: ingestFetchFailuresSchema,
    quarantine: z.boolean(),
    quarantineReason: z.string().nullable(),
    assigned: z.array(ingestStreamTargetSchema),
    running: z.array(ingestStreamTargetSchema),
    mismatchCount: z.number().int().nonnegative(),
    updatedAt: z.number(),
});
export const ingestFleetStatusSchema = z.object({
    coordinator: z
        .object({
        leaderInstanceId: z.string(),
        managedWorkers: z.number(),
        candidateCount: z.number(),
        lastAssignedAt: z.string().nullable(),
        recentFetchFailureAt: z.number().nullable(),
        updatedAt: z.number(),
    })
        .nullable(),
    workers: z.array(ingestFleetWorkerSchema),
    streamProcessors: z.array(z.object({
        instanceId: z.string(),
        donationPerSec: z.number(),
        chatPerSec: z.number(),
        matchedTotal: z.number(),
        insertErrorTotal: z.number(),
        updatedAt: z.number(),
    })),
    suspendedTargets: z.array(ingestSuspendedTargetSchema),
    fleetLoadPerSec: z.number(),
    fleetMeaningfulPerSec: z.number(),
});
export const adminIngestContract = c.router({
    listBroadcasts: {
        method: 'GET',
        path: '/ingest/broadcasts',
        query: z.object({
            filter: broadcastFilterSchema.optional().default('all'),
            page: z.coerce.number().int().positive().optional().default(1),
            pageSize: z.coerce.number().int().positive().max(60).optional().default(24),
        }),
        responses: {
            200: requiredPayloadEnvelope(z.object({
                items: z.array(ingestBroadcastItemSchema),
                meta: ingestBroadcastsMetaSchema,
            })),
            ...commonErrorResponses,
        },
    },
    getThroughput: {
        method: 'GET',
        path: '/ingest/throughput',
        query: z.object({
            /** 화면에 보이는 bjId (쉼표 구분, 최대 60) */
            bjIds: z.string().min(1),
        }),
        responses: {
            200: requiredPayloadEnvelope(z.object({
                throughputTotal: z.number().nullable(),
                collectorOk: z.boolean(),
                items: z.array(z.object({
                    bjId: z.string(),
                    collecting: z.boolean(),
                    throughputEventsPerSec: z.number().nullable(),
                })),
            })),
            ...commonErrorResponses,
        },
    },
    listDonations: {
        method: 'GET',
        path: '/ingest/broadcasts/:bjId/donations',
        pathParams: z.object({ bjId: z.string() }),
        query: z.object({
            limit: z.coerce.number().int().positive().max(200).optional().default(50),
            cursor: z.coerce.number().int().positive().optional(),
        }),
        responses: {
            200: requiredPayloadEnvelope(z.object({
                items: z.array(ingestDonationSchema),
                nextCursor: z.number().nullable(),
                collectorOk: z.boolean(),
            })),
            ...commonErrorResponses,
        },
    },
    getFleetStatus: {
        method: 'GET',
        path: '/ingest/fleet',
        responses: {
            200: requiredPayloadEnvelope(ingestFleetStatusSchema),
            ...commonErrorResponses,
        },
    },
    setWorkerQuarantine: {
        method: 'POST',
        path: '/ingest/fleet/workers/:workerId/quarantine',
        pathParams: z.object({
            workerId: z
                .string()
                .min(1)
                .regex(/^[a-zA-Z0-9][a-zA-Z0-9._-]{0,127}$/, 'workerId 형식이 올바르지 않습니다.'),
        }),
        body: z.object({
            quarantine: z.boolean(),
            reason: z.string().max(500).optional(),
        }),
        responses: {
            200: requiredPayloadEnvelope(z.object({
                workerId: z.string(),
                quarantine: z.boolean(),
            })),
            ...commonErrorResponses,
        },
    },
    suspendTarget: {
        method: 'POST',
        path: '/ingest/fleet/targets/suspend',
        body: z.object({
            platform: z
                .string()
                .trim()
                .min(1)
                .max(64)
                .regex(/^[^:]+$/, 'platform에 콜론을 넣을 수 없습니다.'),
            nativeId: z.string().trim().min(1).max(256),
            reason: z.string().max(500).optional(),
        }),
        responses: {
            200: requiredPayloadEnvelope(ingestSuspendedTargetSchema),
            ...commonErrorResponses,
        },
    },
    resumeTarget: {
        method: 'POST',
        path: '/ingest/fleet/targets/resume',
        body: z.object({
            platform: z
                .string()
                .trim()
                .min(1)
                .max(64)
                .regex(/^[^:]+$/, 'platform에 콜론을 넣을 수 없습니다.'),
            nativeId: z.string().trim().min(1).max(256),
        }),
        responses: {
            200: requiredPayloadEnvelope(z.object({
                platform: z.string(),
                nativeId: z.string(),
                resumed: z.boolean(),
            })),
            ...commonErrorResponses,
        },
    },
});
//# sourceMappingURL=ingest.contract.js.map